/** zh-CN catalogue `pkg` (W3-09; English: src/formats/package/package.messages.ts; terms: docs/i18n/glossary.md). */
import type { MessageCatalog } from '../../core/util/types';

const KIND = '{actual, select, rpe {RPE 谱面} pgr {Phigros 官方谱面} other {PhiEditer 谱面}}';
const FILE_KIND =
  '{kind, select, audio {音频文件} image {图片} video {视频} text {文本文件} other {Phira 无法读取的文件}}';

const catalog: MessageCatalog = {
  ns: 'pkg',
  locale: 'zh-CN',
  messages: {
    'error.badZip': '此文件不是可读取的 zip 压缩包。',
    'error.empty': '没有可导入的文件。',
    'error.noChart':
      '找不到谱面文件。谱面包需要包含 RPE 谱面（.json）、Phigros 官方谱面（.json）或 PhiEditer 谱面（.pec）。',
    'error.pbc': '该谱面是 Phira 的二进制格式（PBC），编辑器无法读取。',
    'error.chartSyntax': '谱面文件不是有效的 JSON（第 {line} 行第 {col} 列）。',
    'error.unexpected': '导入意外失败。请连同文件一起报告此问题。',
    'report.zipNotEnclosed': '压缩包条目“{name}”指向谱面包之外。已略去；Phira 会因此无法加载谱面包。',
    'report.zipMethod':
      '压缩包条目“{name}”使用了压缩方式 {method}，Phira 无法解压（只支持 Stored 和 Deflate）。已略去；Phira 会因此无法加载谱面包。',
    'report.zipEncrypted': '压缩包条目“{name}”已加密。已略去；Phira 会因此无法加载谱面包。',
    'report.zipCorrupt': '压缩包条目“{name}”已损坏。已略去；Phira 会因此无法加载谱面包。',
    'report.zipDuplicate': '压缩包中多次包含“{name}”；已使用最后一份。',
    'report.zipNameEncoding':
      '文件名“{name}”存储时没有 UTF-8 标记（已按 {encoding} 读取）。Phira 读取这类文件名的方式不同，因此在那里对它的引用会失败。导出时使用纯 ASCII 文件名。',
    'report.zipRootFolder':
      '所有文件都在文件夹“{folder}”中，而 Phira 不会解开它：{reason, select, nested {Phira 只解开一层文件夹} noEntry {压缩包没有列出该文件夹本身} other {文件夹旁边还有其他条目（例如系统文件或空文件夹）}}。因此 Phira 找不到谱面，无法加载谱面包。导出时所有文件都放在顶层。',
    'report.nestedArchive':
      '压缩包中只有另一个压缩包“{name}”，已导入其中内容。Phira 不会解开压缩包中的压缩包，因此找不到谱面；Phira 会因此无法加载谱面包。',
    'report.exportJson':
      '谱面包中有 export.json，但其中并没有打包谱面。Phira 会把它当作批量导出；Phira 会因此无法加载谱面包。已略去该文件。',
    'report.formatMisdetected': `Phira 会把该谱面读作{phira, select, rpe {RPE 谱面} pgr {Phigros 官方谱面} pec {PhiEditer 谱面} other {二进制谱面}}，但它其实是${KIND}（它以字节顺序标记或空格开头，或不是 UTF-8 文本）。Phira 会因此无法加载谱面包。`,
    'report.formatMismatch': `清单中说谱面是“{phira}”，但它其实是${KIND}。已按其实际格式读取；Phira 会因此无法加载谱面包。`,
    'report.chartBom': '谱面以字节顺序标记（BOM）开头。已忽略它；Phira 会因此无法加载谱面包。',
    'report.chartEncoding': '谱面不是 UTF-8 文本，已按 {encoding} 读取。Phira 会因此无法加载谱面包。',
    'report.chartLeadingWhitespace':
      '谱面文件以空格或空行开头。因为 info.yml 指明它是 RPE 谱面，Phira 仍能读取它（否则 Phira 无法识别）；已移除这些空白。',
    'report.difficultyInvalid': '难度“{value}”不是数字；已使用 10。',
    'report.aspectRatioInvalid': '宽高比“{value}”不是正数；已使用 16:9。',
    'report.lineLengthInvalid': '判定线长度“{value}”不是正数；已使用 6。',
    'report.backgroundDimClamped': '背景暗度 {value} 超出 0 到 1 的范围，已限制在该范围内。',
    'report.offsetInvalid': '清单中的额外偏移“{value}”不是数字；已使用 0。',
    'report.attachUiFixDefault': '清单没有设置 UI 判定线布局开关。Phira 导入时会将其打开，因此已将其打开。',
    'report.previewClamped':
      '选曲预览（{start} 秒到 {end} 秒）超出了曲目范围（{duration} 秒）。Phira 会拒绝打开这样的谱面；已将预览移到曲目范围内。',
    'report.assetRenamed': '文件“{from}”已重命名为“{to}”（纯 ASCII，且不是 Phira 保留的名称）。',
    'report.assetDuplicate': '文件“{path}”与“{kept}”内容相同；只存储一份，并导出为“{kept}”。',
    'report.missingFile':
      '谱面使用了文件“{path}”，但它不在谱面包中。已添加一个占位文件；请在导出前替换它。Phira 会因此无法加载谱面包。',
    'report.missingFileCase':
      '谱面使用了文件“{path}”，但它不在谱面包中；“{similar}”只是大小写不同，而 Phira 会区分大小写。已添加一个占位文件；Phira 会因此无法加载谱面包。',
    'report.missingFileMoved':
      '谱面使用了文件“{path}”，但它不在谱面包中；Phira 会因此无法加载谱面包。谱面包中以“{found}”存在同一文件；已使用它，导出时会写为“{path}”。',
    'report.missingFileStored':
      '谱面使用了文件“{path}”，但它不在谱面包中；Phira 会因此无法加载谱面包。已改用 phiedit.json 记录的此文件位置“{found}”，导出时会写为“{path}”。',
    'report.restoredMediaReplaced':
      'phiedit.json 中保存的{field, select, music {曲目} other {曲绘}}“{path}”不在谱面包中。Phira 改用谱面包中的“{found}”，因此项目也这样处理。',
    'report.assetChangedOutside':
      '文件“{path}”不是保存 phiedit.json 时的那个文件（它在编辑器之外被修改或添加了）。已使用谱面包中的文件。',
    'report.illustrationMissing': '曲绘“{path}”不在谱面包中。Phira 会改为显示黑色背景；已添加一个占位图。',
    'report.undecodable': '“{path}”（{codec}）是 Phira 无法解码的格式。Phira 会因此无法加载谱面包。请在导出前转换它。',
    'report.undecodableSoft': '曲绘“{path}”（{codec}）是 Phira 无法解码的格式；Phira 会改为显示黑色背景。',
    'report.wrongKind': `“{path}”被用作{role, select, music {曲目} hitsound {打击音} shader {着色器} video {视频} other {判定线图片}}，但它是${FILE_KIND}（{codec}）。Phira 无法在那里使用它，并会因此无法加载谱面包。请将其替换为{role, select, music {音频文件} hitsound {音频文件} shader {GLSL 文本文件} video {视频文件（H.264 MP4）} other {图片}}。`,
    'report.wrongKindSoft': `“{path}”被用作曲绘，但它是${FILE_KIND}（{codec}）。Phira 会改为显示黑色背景。`,
    'report.clickPngRespack':
      '谱面包中包含“{name}”。Phira 会把任何包含以“click.png”结尾的文件名的压缩包当作资源包而不是谱面导入；Phira 会因此无法加载谱面包。导出时永远不会包含这样的文件名（文件会被重命名）。',
    'report.builtinHitsoundShadowed':
      '谱面包中包含“{name}”，但对于使用该打击音名称的音符，Phira 总是播放自己的内置音效。',
    'report.controlOrderSorted':
      '音符{field, select, posControl {位置} alphaControl {不透明度} sizeControl {尺寸} other {滚动距离}}距离控制（{field}）的点没有按距离排序。Phira 读取时会假设它们已排序，因此显示效果取决于之前绘制的音符；已将这些点按距离排序。',
    'report.bpmUnusable': 'BPM {bpm} 太接近 0，Phira 会由此算出无穷大的时间，之后什么也不播放；已改用 {used} BPM。',
    'report.firstKeyJump':
      '一条数值曲线在其第一个点处有瞬间变化（通常是 extra.json 的时间换算为谱面拍数后落在了同一拍上）。现在它从变化后的值开始，Phira 从那里起保持该值。',
    'report.rpeVersionNegative':
      'Phira 将 RPE 版本读作 {value}，这是一个负数（RPE 从不写入负数）。已将其设为 {used}：Phira 只用版本号决定开启“RPE 1.7 流速”时带缓动的流速如何播放，而这两个值的播放方式相同。',
    'report.linkgroupNegative':
      '一个事件的 RPE 绑定组为负数，RPE 从不写入负数；已将其设为 0（无绑定组）。Phira 会忽略绑定组。',
    'report.modelProblems': '导入的项目有 {count} 处内部不一致（第一处：{first}）。请连同文件一起报告此问题。',
    'phiraLint.newSpeedEvents':
      '有 {count, plural, other {# 个流速事件}}使用了缓动。Phira 导入时会对此发出警告：只有开启“RPE 1.7 流速”时，带缓动的流速才会按缓动播放。',
    'phiraLint.attachUi': '有 {count, plural, other {# 条判定线}}绑定到了 UI 元素。Phira 导入时会对此发出警告。',
    'sidecar.restored': '已恢复 phiedit.json 中仅用于编辑器的数据。',
    'sidecar.rejected': '由于其他程序修改了谱面，未应用 phiedit.json 中仅用于编辑器的数据。',
    'sidecar.unreadable': '无法读取 phiedit.json，因此未应用其中仅用于编辑器的数据。',
    'sidecar.newerVersion': 'phiedit.json 由更新版本的编辑器写入（格式 {version}）；未应用其中仅用于编辑器的数据。',
    'sidecar.invalidDoc': 'phiedit.json 中保存的项目已损坏（{detail}）；未应用其中仅用于编辑器的数据。',
    'export.rpeVersionSubMode':
      '使用 RPE 版本 {to} 而不是 {from} 时，Phira 播放 {count, plural, other {# 个带缓动的流速事件}}的方式是{mode, select, modern {速度跟随绘制的曲线（RPE 1.7 积分）} other {使用 RPE < 1.7 的积分（speed = k·f′(x) + b）}}，与预览显示的效果不同。',
    'switch.useRpe170Speed': '“RPE 1.7 流速”（带缓动的流速）已开启；没有 info.yml 时 Phira 会以线性播放流速。',
    'switch.aspectRatio': '游玩区域的宽高比为 {value}，而不是 16:9。',
    'switch.forceAspectRatio': '宽高比已强制固定（在所有屏幕上加黑边）。',
    'switch.lineLength': '普通判定线长 {value} 单位，而不是 6。',
    'switch.backgroundDim': '背景暗度为 {value}，而不是 0.6。',
    'switch.holdPartialCover': '穿过判定线下沉的 Hold 保持可见（Hold 部分遮罩）。',
    'switch.noteUniformScale': '音符大小也会缩放音符高度（音符等比缩放）。',
    'switch.useAttachUiFixOff': 'UI 判定线使用旧布局（UI 判定线修正已关闭）。',
    'switch.infoOffset': 'info.yml 中存储了 {ms} 毫秒的额外偏移。',
    'switch.extraJson': '着色器特效和背景视频位于 extra.json 中，不属于 chart.json。',
    'report.notesReordered':
      '{lines, plural, =1 {一条判定线的音符} other {# 条判定线的音符}}没有按时间顺序列出。项目会按时间顺序保存它们，因此下次导出时也会按此顺序写入；Phira 会自行排序音符，因此其播放内容不会改变。',
    'report.missingFileRenamed':
      '谱面引用了缺失的文件“{from}”。项目期望它的文件名为“{to}”（纯 ASCII 名称，且不与谱面包中的其他文件重名，不区分大小写），下次导出时会引用该名称。',
  },
};

export default catalog;
